import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Share2, Copy, Check, History, Presentation } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { saveScenario } from '../lib/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import logger from '../lib/logger';

export const SaveScenarioDialog = ({ 
  trigger, 
  defaultName = '',
  analysisType = 'single' // 'single', 'portfolio', or 'matrix'
}) => {
  const navigate = useNavigate();
  const { configuration, results, narrative, setCurrentScenario, parentScenario, setParentScenario } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedScenario, setSavedScenario] = useState(null);
  const [copied, setCopied] = useState(false);

  // Use parent scenario name as default if this is a re-run
  const effectiveName = name || (parentScenario ? parentScenario.name : '');

  const handleSave = async () => {
    const scenarioName = effectiveName.trim();
    if (!scenarioName) {
      toast.error('Please enter a scenario name');
      return;
    }

    if (!results) {
      toast.error('No analysis results to save. Run an analysis first.');
      return;
    }

    setIsSaving(true);
    try {
      const scenarioData = {
        name: scenarioName,
        description: description.trim() || `${analysisType} analysis - ${configuration.lei_improvement}% LEI improvement`,
        configuration,
        results,
        narrative: narrative || null,
        analysis_type: analysisType,
        // Include parent scenario info for linking (trend tracking)
        parent_scenario_id: parentScenario?.id || null,
        scenario_group_id: parentScenario?.scenario_group_id || null,
      };

      const response = await saveScenario(scenarioData);
      
      // Update current scenario in context
      setCurrentScenario({
        id: response.data.id,
        name: response.data.name,
        share_token: response.data.share_token,
        scenario_group_id: response.data.scenario_group_id,
      });
      
      // Clear parent scenario since we've now saved the linked version
      if (parentScenario) {
        setParentScenario(null);
      }

      // Store saved scenario for sharing
      setSavedScenario(response.data);
      
      if (parentScenario) {
        toast.success('Linked scenario version saved! View in Trend Analysis to track changes over time.');
      } else {
        toast.success('Scenario saved successfully!');
      }
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!savedScenario) return;
    const shareUrl = `${window.location.origin}/scenarios/${savedScenario.share_token}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setName('');
    setDescription('');
    setSavedScenario(null);
    setCopied(false);
  };

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setOpen(true);
    } else {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="save-scenario-trigger">
            <Save className="mr-2 h-4 w-4" />
            Save Scenario
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        {savedScenario ? (
          // Success state with share options
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <Check className="h-5 w-5" />
                Scenario Saved!
              </DialogTitle>
              <DialogDescription>
                "{savedScenario.name}" has been saved. Share it with colleagues or close to continue.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Share Link */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Share Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={`${window.location.origin}/scenarios/${savedScenario.share_token}`}
                    readOnly
                    className="flex-1 text-sm bg-slate-50"
                    data-testid="saved-share-url"
                  />
                  <Button
                    variant="outline"
                    onClick={handleCopyLink}
                    className={copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : ''}
                    data-testid="copy-share-link-btn"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Anyone with this link can view and load this analysis.
                </p>
              </div>
              
              {/* Quick Summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700 mb-2">Saved Analysis</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Type:</span>{' '}
                    <span className="font-medium capitalize">{analysisType}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">ROI:</span>{' '}
                    <span className="font-medium text-teal-600">
                      {((results?.roi_percent || results?.total_roi_percent || 0) / 100).toFixed(1)}x
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  handleClose();
                  navigate(`/board-ready/${savedScenario.id}`);
                }}
                className="w-full sm:w-auto"
              >
                <Presentation className="mr-2 h-4 w-4" />
                View Board Ready
              </Button>
              <Button onClick={handleClose} className="w-full sm:w-auto bg-navy-700 hover:bg-navy-800">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Initial save form
          <>
            <DialogHeader>
              <DialogTitle>
                {parentScenario ? 'Save Linked Version' : 'Save Analysis Scenario'}
              </DialogTitle>
              <DialogDescription>
                {parentScenario ? (
                  <>
                    This will be saved as a new version of <strong>"{parentScenario.name}"</strong>, 
                    linked for trend tracking. You can view changes over time in Trend Analysis.
                  </>
                ) : (
                  'Save your current analysis configuration and results for future reference or sharing.'
                )}
              </DialogDescription>
            </DialogHeader>
            
            {/* Parent Scenario Info Banner */}
            {parentScenario && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center gap-2">
                <History className="h-4 w-4 text-teal-600" />
                <div className="text-sm">
                  <span className="font-medium text-teal-800">Linking to:</span>{' '}
                  <span className="text-teal-700">{parentScenario.name}</span>
                </div>
              </div>
            )}
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="scenario-name">Scenario Name *</Label>
                <Input
                  id="scenario-name"
                  value={name || (parentScenario ? parentScenario.name : '')}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={parentScenario ? parentScenario.name : "e.g., Q1 Training Impact Analysis"}
                  data-testid="scenario-name-input"
                />
                {parentScenario && !name && (
                  <p className="text-xs text-slate-500">
                    Using original scenario name. Change if you want a different name.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="scenario-description">Description (optional)</Label>
                <Textarea
                  id="scenario-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add notes about this analysis..."
                  rows={3}
                  data-testid="scenario-description-input"
                />
              </div>
              
              {/* Quick Summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700 mb-2">Analysis Summary</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">LEI Improvement:</span>{' '}
                    <span className="font-medium">{configuration.lei_improvement}%</span>
                  </div>
                  <div>
                    <span className="text-slate-500">ROI:</span>{' '}
                    <span className="font-medium text-teal-600">
                      {((results?.roi_percent || results?.total_roi_percent || 0) / 100).toFixed(1)}x
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Financial Impact:</span>{' '}
                    <span className="font-medium">
                      ${Math.round(results?.financial_impact || results?.total_financial_impact || 0).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Analysis Type:</span>{' '}
                    <span className="font-medium capitalize">{analysisType}</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={isSaving || !results}
                className="bg-navy-700 hover:bg-navy-800"
                data-testid="save-scenario-confirm"
              >
                {isSaving ? (
                  <>
                    <div className="spinner mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Scenario
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaveScenarioDialog;
